import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { BootstrapData, DashboardReport } from "@czasomat/contracts";
import { AlertTriangle, CalendarClock, CheckCircle2, Clock3, Moon, TimerOff } from "lucide-react";
import { api } from "../api/client";
import { formatMinutes } from "../components/ui";

export function DashboardPage({ data }: { data: BootstrapData }) {
  const [period, setPeriod] = useState<"day" | "week" | "month">("month");
  const permissions = data.auth.permissions;
  const canRead = permissions.includes("all") || permissions.includes("self.read") || permissions.includes("reports.read");
  const queryClient = useQueryClient();
  const queryKey = ["dashboard", data.auth.id, data.auth.employeeId, [...permissions].sort().join(","), period, data.today, data.settings.timeZone];
  const query = useQuery({
    queryKey,
    queryFn: () => api<DashboardReport>(`/api/v1/dashboard?period=${period}`),
    staleTime: 0,
    enabled: canRead
  });
  const inputs = [data.timeEvents, data.schedules, data.workStatuses, data.settings, data.employees];
  const previousInputs = useRef({ userId: data.auth.id, inputs });
  useEffect(() => {
    const previous = previousInputs.current;
    previousInputs.current = { userId: data.auth.id, inputs };
    // RCP and other editors refresh bootstrap. Refresh this separate aggregate too,
    // while query keys handle account, permission, date and period changes.
    if (canRead && previous.userId === data.auth.id && inputs.some((value, index) => value !== previous.inputs[index])) {
      void queryClient.cancelQueries({ queryKey, exact: true }).then(() =>
        queryClient.refetchQueries({ queryKey, exact: true, type: "active" })
      );
    }
  }, [data.auth.id, data.timeEvents, data.schedules, data.workStatuses, data.settings, data.employees, canRead, queryClient]);
  const firstName = data.auth.name.split(" ")[0] || data.auth.name;
  const report = query.data;
  const date = new Intl.DateTimeFormat("pl-PL", { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "UTC" })
    .format(new Date(`${report?.today ?? data.today}T12:00:00Z`));
  const stats = report ? [
    { label: "Plan opublikowany", value: report.plannedMinutes === null ? "Brak poprawnej sumy" : formatMinutes(report.plannedMinutes), icon: CalendarClock },
    { label: "Czas pracy", value: formatMinutes(report.workMilliseconds / 60_000), icon: CheckCircle2 },
    { label: "Norma bazowa", value: formatMinutes(report.normMinutes), icon: Clock3 },
    { label: "Brak do normy bazowej", value: formatMinutes(report.missingNormMinutes), icon: TimerOff },
    { label: "Ponad normę bazową", value: formatMinutes(report.overtimeMinutes), icon: Clock3 },
    { label: "Praca w godz. nocnych", value: "Brak naliczeń", icon: Moon },
    { label: "Praca poza planem", value: "Brak naliczeń", icon: AlertTriangle }
  ] : [];

  return (
    <div className="dashboard-page page-content">
      <header className="dashboard-greeting">
        <h1>Dzień dobry, {firstName}!</h1>
        <p>Dziś jest {date}.</p>
      </header>
      <section className="dashboard-section">
        <div className="section-heading">
          <h2>{report?.scope === "team" ? "Statystyki dostępnego zespołu" : "Twoje statystyki"}</h2>
          <select aria-label="Okres statystyk" value={period} onChange={(event) => setPeriod(event.target.value as typeof period)} disabled={!canRead}>
            <option value="day">Dzień</option><option value="week">Tydzień do dziś</option><option value="month">Miesiąc do dziś</option>
          </select>
        </div>
        {!canRead ? <p role="alert" className="form-error">Brak uprawnień do statystyk czasu pracy.</p>
          : query.isPending || query.isFetching ? <p role="status">Wczytywanie statystyk…</p>
          : query.error ? <p role="alert" className="form-error">Nie udało się pobrać statystyk: {query.error instanceof Error ? query.error.message : "Nieznany błąd"}</p>
          : report ? <>
            <p>{report.from} – {report.to} · {report.timeZone} · Osoby: {report.employeeCount}</p>
            {report.invalidScheduleCount > 0 ? <p role="alert" className="form-error">Nie można obliczyć pełnego planu. Zmiany z nieprawidłową datą lub godziną: {report.invalidScheduleCount}.</p> : null}
            {report.invalidAttendanceDayCount > 0 ? <p role="alert" className="form-error">Dni z błędami rejestracji: {report.invalidAttendanceDayCount}. Suma czasu wymaga sprawdzenia szczegółów w ewidencji.</p> : null}
            <div className="statistics-list">
              {stats.map(({ label, value, icon: Icon }) => (
                <div className="statistic-row" key={label}>
                  <span className="statistic-row__label"><Icon size={18} />{label}</span>
                  <strong>{value}</strong>
                </div>
              ))}
            </div>
            <p>Czas pracy to suma ukończonych odcinków przypisanych do daty START przed zaokrągleniem, również ze STOP w kolejnym dniu lub miesiącu. Brak odcinka nie potwierdza zerowego czasu pracy. Plan to nominalne godziny opublikowanych zmian rozpoczynających się w okresie. Norma bazowa pochodzi z ustawień i jest liczona dla dni kalendarzowych. Nie stanowi pełnych naliczeń KP ani oceny usprawiedliwienia nieobecności.</p>
          </> : null}
      </section>
    </div>
  );
}
